
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PenSquare } from "lucide-react";
import { Priority } from "@/types/habit";
import { MEASUREMENT_UNITS, formatUnit } from "@/utils/habitUtils";
import { useToast } from "@/hooks/use-toast";

interface HabitEditPopoverProps {
  habit: {
    id: string;
    title: string;
    priority: Priority;
    category: string[];
    dailyTarget?: {
      value: number;
      unit: string;
    };
  };
  availableCategories: string[];
  onUpdateTitle: (id: string, title: string) => void;
  onUpdatePriority: (id: string, priority: Priority) => void;
  onUpdateCategories: (id: string, categories: string[]) => void;
  onUpdateDailyTarget: (id: string, value: number, unit: string) => void;
  onDelete: (id: string) => void;
  handleAddNewCategoryToSystem: (category: string) => void;
}

export const HabitEditPopover = ({
  habit,
  availableCategories,
  onUpdateTitle,
  onUpdatePriority,
  onUpdateCategories,
  onUpdateDailyTarget,
  onDelete,
  handleAddNewCategoryToSystem
}: HabitEditPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(habit.title);
  const [newTargetValue, setNewTargetValue] = useState(habit.dailyTarget?.value.toString() || "");
  const [newTargetUnit, setNewTargetUnit] = useState(habit.dailyTarget?.unit || "");
  const [newCategory, setNewCategory] = useState("");
  const [selectedPriority, setSelectedPriority] = useState(habit.priority);
  const { toast } = useToast();

  const availableCategoriesFiltered = availableCategories.filter(
    cat => !habit.category.includes(cat)
  );

  const handleAddCategory = (category: string) => {
    if (habit.category.includes(category)) {
      // If already included, remove it
      const updatedCategories = habit.category.filter(cat => cat !== category);
      onUpdateCategories(habit.id, updatedCategories);
      
      toast({
        title: "Category removed",
        description: `Removed "${category}" from this habit`,
      });
    } else if (habit.category.length < 3) {
      // If not included and we have space, add it
      const updatedCategories = [...habit.category, category];
      onUpdateCategories(habit.id, updatedCategories);
      
      toast({
        title: "Category added",
        description: `Added "${category}" to this habit`,
      });
    } else {
      toast({
        title: "Category limit reached",
        description: "You can only add up to 3 categories per habit.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    const updatedCategories = habit.category.filter(cat => cat !== categoryToRemove);
    onUpdateCategories(habit.id, updatedCategories);
    
    toast({
      title: "Category removed",
      description: `Removed "${categoryToRemove}" from this habit`,
    });
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim() && !habit.category.includes(newCategory.trim()) && habit.category.length < 3) {
      const categoryName = newCategory.trim();
      
      // First add to categories system
      handleAddNewCategoryToSystem(categoryName);
      
      // Then update the habit's categories
      onUpdateCategories(habit.id, [...habit.category, categoryName]);
      setNewCategory("");
      
      toast({
        title: "Category created and added",
        description: `New category "${categoryName}" created and added to this habit`,
      });
    }
  };

  const handlePriorityChange = (newPriority: Priority) => {
    setSelectedPriority(newPriority); // Update local state immediately
    onUpdatePriority(habit.id, newPriority);
    toast({
      title: "Priority updated",
      description: `Priority has been set to ${newPriority}`,
    });
  };

  const handleSaveChanges = () => {
    // Update title if changed
    if (newTitle.trim() && newTitle !== habit.title) {
      onUpdateTitle(habit.id, newTitle.trim());
    }

    // Update daily target if both value and unit are set
    const value = parseFloat(newTargetValue);
    if (!isNaN(value) && value > 0 && newTargetUnit) {
      onUpdateDailyTarget(habit.id, value, newTargetUnit);
    }

    toast({
      title: "Success",
      description: "All changes have been saved successfully.",
    });

    // Close the popover
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <PenSquare className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="left" className="w-80">
        <div className="space-y-4 p-1">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Edit Habit Name</Label>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter new title"
              className="border-primary/30 focus:border-primary rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Update Daily Target</Label>
            <div className="flex space-x-2">
              <Input
                type="number"
                value={newTargetValue}
                onChange={(e) => setNewTargetValue(e.target.value)}
                placeholder="Enter value"
                className="flex-1 border-primary/30 focus:border-primary rounded-xl"
              />
              <select
                value={newTargetUnit}
                onChange={(e) => setNewTargetUnit(e.target.value)}
                className="border rounded-xl px-3 py-2 bg-white text-gray-900 focus:ring-primary focus:border-primary min-w-[100px] appearance-none cursor-pointer hover:border-primary/50 transition-colors"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="">Unit</option>
                {MEASUREMENT_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {formatUnit(unit)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Priority</Label>
            <div className="flex gap-2">
              <Button
                variant={selectedPriority === "high" ? "default" : "outline"}
                onClick={() => handlePriorityChange("high")}
                className={cn(
                  "flex-1 transition-all",
                  selectedPriority === "high"
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "border-primary text-gray-700 hover:bg-primary/10"
                )}
              >
                High
              </Button>
              <Button
                variant={selectedPriority === "medium" ? "default" : "outline"}
                onClick={() => handlePriorityChange("medium")}
                className={cn(
                  "flex-1 transition-all",
                  selectedPriority === "medium"
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "border-primary text-gray-700 hover:bg-primary/10"
                )}
              >
                Medium
              </Button>
              <Button
                variant={selectedPriority === "low" ? "default" : "outline"}
                onClick={() => handlePriorityChange("low")}
                className={cn(
                  "flex-1 transition-all",
                  selectedPriority === "low"
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "border-primary text-gray-700 hover:bg-primary/10"
                )}
              >
                Low
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Categories (Max 3)</Label>
            <div className="flex flex-wrap gap-2">
              {habit.category.map((cat, index) => (
                <Badge
                  key={index}
                  className="bg-primary text-white text-xs cursor-pointer hover:bg-primary/80 transition-colors"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCategory(cat);
                  }}
                >
                  {cat} ✕
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {availableCategoriesFiltered.map((cat) => (
                <Badge
                  key={cat}
                  className={cn(
                    "cursor-pointer transition-all",
                    habit.category.includes(cat)
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                  )}
                  variant="secondary"
                  onClick={() => handleAddCategory(cat)}
                >
                  {cat}
                </Badge>
              ))}
            </div>
            <div className="flex space-x-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Enter new category"
                className="flex-1 border-primary/30 focus:border-primary rounded-xl"
              />
              <Button
                onClick={handleAddNewCategory}
                disabled={!newCategory.trim() || habit.category.length >= 3}
                className="bg-primary text-white hover:bg-primary/90"
              >
                Add
              </Button>
            </div>
          </div>

          <Button
            onClick={handleSaveChanges}
            className="w-full bg-primary text-white hover:bg-primary/90 mt-4"
          >
            Save Changes
          </Button>

          <Button
            onClick={() => onDelete(habit.id)}
            variant="outline"
            className="w-full border-[#ea384c] text-[#ea384c] hover:bg-[#ea384c] hover:text-white mt-2"
          >
            Delete Habit
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
